import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { rupee, apiError } from "@/lib/api";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShoppingCart, IndianRupee, Store, Users, Bike, Tag, Image as ImageIcon, Plus, Trash2, LifeBuoy, ScrollText, Smartphone, Upload, PackageCheck, Headset, UserPlus, KeyRound, Settings, BadgeCheck, TrendingUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Spinner, EmptyState } from "@/components/States";
import { AdminSubscriptions, AdminSubPayments } from "@/pages/admin/AdminSubscriptions";
import AdminRetailers from "@/pages/admin/AdminRetailers";
import { AdminCategories, AdminStores, AdminProducts, AdminPayments } from "@/pages/admin/AdminCatalog";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import { AdminMasterProducts, AdminApprovals, AdminTaxonomy } from "@/pages/admin/AdminCatalogue";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminSubAdmins from "@/pages/admin/AdminSubAdmins";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSurveyCRM from "@/pages/admin/AdminSurveyCRM";
import AdminAutoImport from "@/pages/admin/AdminAutoImport";
import AdminShyama from "@/pages/admin/AdminShyama";
import AdminMembership from "@/pages/admin/AdminMembership";
import AdminCommunity from "@/pages/admin/AdminCommunity";

export default function AdminDashboard() {
  const [range, setRange] = useState("30");
  const [tab, setTab] = useState("retailers");
  const { data: d } = useQuery({ queryKey: ["a-dash", range], queryFn: () => api.get("/admin/dashboard", { params: { range } }).then((r) => r.data), refetchInterval: 8000 });
  const { data: charts } = useQuery({ queryKey: ["a-charts"], queryFn: () => api.get("/admin/charts").then((r) => r.data) });
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isSuper = user?.role === "admin";
  const can = (m) => isSuper || (m !== "SUPER" && perms.includes(m));
  const TABS = [
    { value: "retailers", label: "Retailers", module: "retailer_management", el: <AdminRetailers /> },
    { value: "reports", label: "Reports", module: "reports", el: <AdminReports /> },
    { value: "affiliates", label: "Affiliates", module: "affiliate_management", el: <AdminAffiliates /> },
    { value: "catalogue", label: "Product Master", module: "product_management", el: <AdminMasterProducts /> },
    { value: "autoimport", label: "Auto Import", module: "product_management", el: <AdminAutoImport /> },
    { value: "approvals", label: "Approvals", module: "product_management", el: <AdminApprovals /> },
    { value: "taxonomy", label: "Brands & Attrs", module: "product_management", el: <AdminTaxonomy /> },
    { value: "stores", label: "Stores", module: "retailer_management", el: <AdminStores /> },
    { value: "products", label: "Products", module: "product_management", el: <AdminProducts /> },
    { value: "categories", label: "Categories", module: "product_management", el: <AdminCategories /> },
    { value: "admpayments", label: "Payments", module: "payments", el: <AdminPayments /> },
    { value: "subscriptions", label: "Subscriptions", module: "retailer_management", el: <AdminSubscriptions /> },
    { value: "payments", label: "Sub. Payments", module: "payments", el: <AdminSubPayments /> },
    { value: "customers", label: "Customers", module: "customer_management", el: <AdminCustomers /> },
    { value: "orders", label: "Orders", module: "order_management", el: <AdminOrders /> },
    { value: "delivery", label: "Delivery", module: "order_management", el: <AdminDelivery /> },
    { value: "coupons", label: "Coupons", module: "marketing", el: <AdminCoupons /> },
    { value: "leads", label: "Store Leads", module: "marketing", el: <AdminLeads /> },
    { value: "membership", label: "Membership", module: "marketing", el: <AdminMembership /> },
    { value: "community", label: "Community", module: "marketing", el: <AdminCommunity /> },
    { value: "surveycrm", label: "Survey CRM", module: "survey_crm", el: <AdminSurveyCRM /> },
    { value: "shyama", label: "Shyama Foundation", module: "shyama", el: <AdminShyama /> },
    { value: "verification", label: "Verification Fees", module: "retailer_management", el: <AdminVerification /> },
    { value: "banners", label: "Banners", module: "marketing", el: <AdminBanners /> },
    { value: "testimonials", label: "Testimonials", module: "marketing", el: <AdminTestimonials /> },
    { value: "faqs", label: "FAQs", module: "marketing", el: <AdminFAQs /> },
    { value: "support", label: "Support", module: "support", el: <AdminSupport /> },
    { value: "agents", label: "Support Agents", module: "support", el: <AdminAgents /> },
    { value: "subadmins", label: "Sub-Admins", module: "SUPER", el: <AdminSubAdmins /> },
    { value: "app", label: "App / PWA", module: "SUPER", el: <AdminApp /> },
    { value: "settings", label: "Fees & Trial", module: "SUPER", el: <AdminSettings /> },
    { value: "audit", label: "Audit Logs", module: "SUPER", el: <AdminAudit /> },
  ];
  const visibleTabs = TABS.filter((t) => can(t.module));
  const go = (t) => { if (visibleTabs.some((v) => v.value === t)) setTab(t); };
  React.useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((v) => v.value === tab)) setTab(visibleTabs[0].value);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardShell title="Admin Command Center" roleColor="#8B5CF6" roleLabel="Admin">
      {!d ? <div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div> : (
        <>
          <div data-testid="admin-kpi-strip" className="mb-5 grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-gradient-to-r from-brand-deep to-brand p-4 text-white sm:grid-cols-4">
            <KpiPill label="Gross Order Value" value={rupee(d.gmv)} icon={IndianRupee} testid="admin-kpi-gmv" onClick={() => go("reports")} />
            <KpiPill label="Orders Today" value={d.todays_orders} icon={ShoppingCart} testid="admin-kpi-orders-today" onClick={() => go("orders")} />
            <KpiPill label="Active Stores" value={`${d.active_stores_visible}/${d.total_retailers}`} icon={Store} testid="admin-kpi-active-stores" onClick={() => go("stores")} />
            <KpiPill label="Avg. Order Value" value={rupee(d.avg_order_value)} icon={TrendingUp} testid="admin-kpi-aov" onClick={() => go("reports")} />
          </div>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-500">Business overview</p>
            <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="dash-date-filter" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 outline-none">
              <option value="1">Today</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </select>
          </div>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard testid="admin-stats-total-orders" label="Total Orders" value={d.total_orders} icon={ShoppingCart} color="#8B5CF6" />
            <StatCard testid="admin-stats-total-gmv" label="Gross Order Value" value={rupee(d.gmv)} icon={IndianRupee} color="#059669" />
            <StatCard testid="admin-stats-active-retailers" label="Active Retailers" value={`${d.active_retailers}/${d.total_retailers}`} icon={Store} color="#3B82F6" />
            <StatCard label="Customers" value={`${d.active_customers}/${d.total_customers}`} icon={Users} color="#F59E0B" />
            <StatCard label="Orders Today" value={d.todays_orders} icon={ShoppingCart} color="#8B5CF6" />
            <StatCard label="Orders (7d / 30d)" value={`${d.orders_week} / ${d.orders_month}`} icon={ShoppingCart} color="#3B82F6" />
            <StatCard label="Pending Orders" value={d.pending_orders} icon={ShoppingCart} color="#F59E0B" />
            <StatCard label="Completed / Cancelled" value={`${d.completed_orders} / ${d.cancelled_orders}`} icon={ShoppingCart} color="#059669" />
            <StatCard label="Platform Revenue" value={rupee(d.platform_revenue)} icon={IndianRupee} color="#059669" />
            <StatCard label="Retailer Delivery Rev." value={rupee(d.retailer_delivery_revenue)} icon={IndianRupee} color="#3B82F6" />
            <StatCard label="Subscription Rev. (MRR)" value={rupee(d.subscription_revenue)} icon={IndianRupee} color="#8B5CF6" />
            <StatCard label="Pending Approvals" value={d.pending_approvals} icon={Store} color="#F59E0B" />
            <StatCard label="Suspended Retailers" value={d.suspended_retailers} icon={Store} color="#EF4444" />
            <StatCard label="Stores Visible" value={d.active_stores_visible} icon={Store} color="#059669" />
            <StatCard label="Stores Hidden" value={d.stores_hidden} icon={Store} color="#EF4444" />
            <StatCard label="In Grace Period" value={d.pending_subscription_payments} icon={IndianRupee} color="#F59E0B" />
            <StatCard label="Delivery Online" value={`${d.online_delivery}/${d.total_delivery}`} icon={Bike} color="#F59E0B" />
          </div>

          {charts && (
            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="mb-3 font-head font-bold text-slate-900">Orders (last 7 days)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={charts.daily}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="orders" fill="#8B5CF6" radius={[6, 6, 0, 0]} /></BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="mb-3 font-head font-bold text-slate-900">Revenue (last 7 days)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={charts.daily}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={3} dot={false} /></LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="flex flex-col gap-5 lg:flex-row lg:items-start">
            <TabsList className="flex h-auto w-full flex-row flex-wrap gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 lg:sticky lg:top-24 lg:w-56 lg:shrink-0 lg:flex-col lg:items-stretch lg:overflow-visible lg:[&>button]:w-full lg:[&>button]:justify-start [&>button[data-state=active]]:bg-brand-soft [&>button[data-state=active]]:text-brand [&>button[data-state=active]]:shadow-none">
              {visibleTabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`admin-tab-${t.value}`}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
            <div className="min-w-0 flex-1">
              {visibleTabs.map((t) => (
                <TabsContent key={t.value} value={t.value}>{t.el}</TabsContent>
              ))}
            </div>
          </Tabs>
        </>
      )}
    </DashboardShell>
  );
}

const Table = ({ children }) => <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"><table className="w-full text-sm">{children}</table></div>;
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

const KpiPill = ({ label, value, icon: Icon, testid, onClick }) => (
  <button data-testid={testid} onClick={onClick} type="button"
    className="flex items-center gap-3 rounded-xl text-left transition-transform hover:scale-[1.02] active:scale-95">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15"><Icon className="h-5 w-5" /></span>
    <div className="min-w-0">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-white/70">{label}</p>
      <p className="font-head text-lg font-black leading-tight">{value}</p>
    </div>
  </button>
);

function AdminCustomers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-customers"], queryFn: () => api.get("/admin/customers").then((r) => r.data) });
  const toggle = async (id) => { await api.patch(`/admin/customers/${id}/toggle`); qc.invalidateQueries({ queryKey: ["a-customers"] }); toast.success("Updated"); };
  if (!data) return <Spinner />;
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Status</Th><Th></Th></tr></thead>
      <tbody>
        {data.map((c) => (
          <tr key={c.id} className="border-b border-slate-50">
            <Td className="font-semibold text-slate-800">{c.name}</Td><Td className="text-slate-500">{c.email}</Td><Td className="text-slate-500">{c.phone || "-"}</Td>
            <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${c.is_active ? "bg-brand-soft text-brand" : "bg-red-50 text-red-500"}`}>{c.is_active ? "Active" : "Blocked"}</span></Td>
            <Td><button onClick={() => toggle(c.id)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">{c.is_active ? "Block" : "Unblock"}</button></Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AdminOrders() {
  const { data } = useQuery({ queryKey: ["a-orders"], queryFn: () => api.get("/admin/orders").then((r) => r.data), refetchInterval: 8000 });
  if (!data) return <Spinner />;
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Order</Th><Th>Customer</Th><Th>Store</Th><Th>Amount</Th><Th>Status</Th></tr></thead>
      <tbody>
        {data.map((o) => (
          <tr key={o.id} className="border-b border-slate-50">
            <Td className="font-semibold text-slate-800">#{o.order_no}</Td><Td className="text-slate-500">{o.customer_name}</Td><Td className="text-slate-500">{o.store_name}</Td>
            <Td className="font-mono">{rupee(o.total)}</Td>
            <Td><span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold capitalize text-slate-600">{o.status.replace(/_/g, " ")}</span></Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AdminDelivery() {
  const { data } = useQuery({ queryKey: ["a-delivery"], queryFn: () => api.get("/admin/delivery").then((r) => r.data), refetchInterval: 8000 });
  if (!data) return <Spinner />;
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Name</Th><Th>Vehicle</Th><Th>Status</Th><Th>Active</Th><Th>Earnings</Th></tr></thead>
      <tbody>
        {data.map((p) => (
          <tr key={p.id} className="border-b border-slate-50">
            <Td className="font-semibold text-slate-800">{p.name}</Td><Td className="text-slate-500">{p.vehicle || "-"}</Td>
            <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${p.is_online ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-500"}`}>{p.is_online ? "Online" : "Offline"}</span></Td>
            <Td>{p.active_deliveries}</Td><Td className="font-mono">{rupee(p.earnings || 0)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AdminCoupons() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-coupons"], queryFn: () => api.get("/admin/coupons").then((r) => r.data) });
  const [form, setForm] = useState({ code: "", discount_type: "fixed", value: "", min_order: "", max_discount: "100" });
  const add = async () => { try { await api.post("/admin/coupons", { ...form, value: +form.value, min_order: +form.min_order, max_discount: +form.max_discount }); qc.invalidateQueries({ queryKey: ["a-coupons"] }); toast.success("Coupon created"); setForm({ code: "", discount_type: "fixed", value: "", min_order: "", max_discount: "100" }); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); qc.invalidateQueries({ queryKey: ["a-coupons"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="fixed">Fixed ₹</option><option value="percent">Percent %</option></select>
        <input placeholder="Value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Min order" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={add} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Code</Th><Th>Scope</Th><Th>Type</Th><Th>Value</Th><Th>Min Order</Th><Th></Th></tr></thead>
        <tbody>
          {data.map((c) => (
            <tr key={c.id} className="border-b border-slate-50">
              <Td className="font-mono font-bold text-brand">{c.code}</Td>
              <Td className="text-slate-500">{c.store_name ? c.store_name : <span className="rounded-md bg-brand-soft px-2 py-0.5 text-[11px] font-bold text-brand">Global</span>}</Td>
              <Td className="capitalize text-slate-500">{c.discount_type}</Td>
              <Td>{c.discount_type === "fixed" ? rupee(c.value) : `${c.value}%`}</Td><Td>{rupee(c.min_order)}</Td>
              <Td><button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function AdminBanners() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-banners"], queryFn: () => api.get("/admin/banners").then((r) => r.data) });
  const empty = { title: "", subtitle: "", image: "", image_mobile: "", cta_text: "Shop Now", cta_url: "/stores", start_date: "", end_date: "", order: 0, is_active: true };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(0);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadImg = async (file, field) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload/image", fd); set(field, r.data.url); toast.success("Image uploaded"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    if (!form.title || !form.image) return toast.error("Title and desktop image are required");
    const body = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing) await api.patch(`/admin/banners/${editing}`, body);
      else await api.post("/admin/banners", body);
      qc.invalidateQueries({ queryKey: ["a-banners"] }); qc.invalidateQueries({ queryKey: ["banners"] });
      toast.success(editing ? "Banner updated" : "Banner added"); setForm(empty); setEditing(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const edit = (b) => { setEditing(b.id); setForm({ ...empty, ...b }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const toggle = async (b) => { await api.patch(`/admin/banners/${b.id}`, { is_active: !b.is_active }); qc.invalidateQueries({ queryKey: ["a-banners"] }); qc.invalidateQueries({ queryKey: ["banners"] }); };
  const del = async (id) => { await api.delete(`/admin/banners/${id}`); qc.invalidateQueries({ queryKey: ["a-banners"] }); qc.invalidateQueries({ queryKey: ["banners"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;

  const dfmt = (v) => (v ? v.slice(0, 10) : "");
  const slides = data.filter((b) => b.is_active);
  const cur = slides[preview % (slides.length || 1)];

  const Img = ({ field, label }) => (
    <div>
      <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        {form[field] && <img src={form[field]} alt="" className="h-10 w-16 rounded object-cover" />}
        <input placeholder="Image URL" value={form[field]} onChange={(e) => set(field, e.target.value)}
          data-testid={field === "image" ? "admin-banner-image-desktop" : "admin-banner-image-mobile"}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <label className="cursor-pointer rounded-lg border border-slate-200 px-2 py-2 text-xs text-slate-500 hover:bg-slate-50">
          <Upload className="h-4 w-4" />
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0], field)} />
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-banner-management-panel">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 font-head font-bold text-slate-900">{editing ? "Edit banner" : "Add homepage banner"}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input placeholder="Headline / title" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-banner-title-input" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input placeholder="Subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="admin-banner-subtitle-input" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <Img field="image" label="Desktop image" />
          <Img field="image_mobile" label="Mobile image (optional)" />
          <input placeholder="CTA text (e.g. Shop Now)" value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} data-testid="admin-banner-cta-input" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input placeholder="Redirect URL (/stores, /category/..)" value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} data-testid="admin-banner-redirect-input" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Start date</span>
            <input type="date" value={dfmt(form.start_date)} onChange={(e) => set("start_date", e.target.value ? new Date(e.target.value).toISOString() : "")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
          <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">End date</span>
            <input type="date" value={dfmt(form.end_date)} onChange={(e) => set("end_date", e.target.value ? new Date(e.target.value).toISOString() : "")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
          <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Display priority</span>
            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="admin-banner-priority-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></label>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="admin-banner-toggle-active" /> Active</label>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={save} data-testid="admin-banner-save-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-brand-deep"><Plus className="h-4 w-4" /> {editing ? "Update banner" : "Add banner"}</button>
          {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>}
        </div>
      </div>

      {slides.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-banner-preview-slider">
          <h3 className="mb-3 font-head font-bold text-slate-900">Live slider preview</h3>
          <div className="relative overflow-hidden rounded-2xl bg-brand-deep">
            <img src={cur?.image} alt="" className="h-40 w-full object-cover object-right opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-deep to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center p-6 text-white">
              <p className="font-head text-xl font-black">{cur?.title}</p>
              <p className="text-xs text-emerald-50">{cur?.subtitle}</p>
            </div>
          </div>
          <div className="mt-2 flex justify-center gap-1.5">
            {slides.map((_, idx) => <button key={idx} onClick={() => setPreview(idx)} className={`h-1.5 rounded-full ${idx === preview % slides.length ? "w-6 bg-brand" : "w-1.5 bg-slate-300"}`} />)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-banner-table">
        {data.map((b) => (
          <div key={b.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <img src={b.image} alt="" className="h-28 w-full object-cover" />
            <div className="p-3">
              <div className="flex items-start justify-between">
                <div className="min-w-0"><p className="truncate text-sm font-bold text-slate-800">{b.title}</p><p className="truncate text-xs text-slate-400">{b.subtitle}</p></div>
                <span className={`ml-2 shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold ${b.is_active ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-500"}`}>{b.is_active ? "Live" : "Off"}</span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Priority {b.order} · → {b.cta_url}</p>
              <div className="mt-2 flex items-center gap-2">
                <Switch checked={b.is_active} onCheckedChange={() => toggle(b)} />
                <button onClick={() => edit(b)} className="text-xs font-semibold text-brand">Edit</button>
                <button onClick={() => del(b.id)} className="ml-auto text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminTestimonials() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-testimonials"], queryFn: () => api.get("/admin/testimonials").then((r) => r.data) });
  const empty = { name: "", location: "", avatar: "", rating: 5, quote: "", role: "customer", order: 0, is_active: true };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const add = async () => {
    if (!form.name || !form.quote) return toast.error("Name and quote are required");
    try { await api.post("/admin/testimonials", { ...form, rating: Number(form.rating), order: Number(form.order) }); qc.invalidateQueries({ queryKey: ["a-testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials"] }); toast.success("Added"); setForm(empty); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (t) => { await api.patch(`/admin/testimonials/${t.id}`, { is_active: !t.is_active }); qc.invalidateQueries({ queryKey: ["a-testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials"] }); };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); qc.invalidateQueries({ queryKey: ["a-testimonials"] }); qc.invalidateQueries({ queryKey: ["testimonials"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2">
        <input placeholder="Customer name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-testimonial-name" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Location / role" value={form.location} onChange={(e) => set("location", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Avatar image URL (optional)" value={form.avatar} onChange={(e) => set("avatar", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={form.rating} onChange={(e) => set("rating", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">{[5, 4, 3].map((r) => <option key={r} value={r}>{r} stars</option>)}</select>
        <textarea placeholder="Quote / testimonial" value={form.quote} onChange={(e) => set("quote", e.target.value)} data-testid="admin-testimonial-quote" className="sm:col-span-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} />
        <button onClick={add} data-testid="admin-testimonial-add" className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add testimonial</button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between"><p className="text-sm font-bold text-slate-800">{t.name}</p>
              <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${t.is_active ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-500"}`}>{t.is_active ? "Live" : "Off"}</span></div>
            <p className="text-xs text-slate-400">{t.location} · {t.rating}★</p>
            <p className="mt-2 line-clamp-3 text-sm text-slate-600">“{t.quote}”</p>
            <div className="mt-2 flex items-center gap-2"><Switch checked={t.is_active} onCheckedChange={() => toggle(t)} /><button onClick={() => del(t.id)} className="ml-auto text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminFAQs() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-faqs"], queryFn: () => api.get("/admin/faqs").then((r) => r.data) });
  const empty = { question: "", answer: "", order: 0, is_active: true };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const add = async () => {
    if (!form.question || !form.answer) return toast.error("Question and answer are required");
    try { await api.post("/admin/faqs", { ...form, order: Number(form.order) }); qc.invalidateQueries({ queryKey: ["a-faqs"] }); qc.invalidateQueries({ queryKey: ["faqs"] }); toast.success("Added"); setForm(empty); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (f) => { await api.patch(`/admin/faqs/${f.id}`, { is_active: !f.is_active }); qc.invalidateQueries({ queryKey: ["a-faqs"] }); qc.invalidateQueries({ queryKey: ["faqs"] }); };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); qc.invalidateQueries({ queryKey: ["a-faqs"] }); qc.invalidateQueries({ queryKey: ["faqs"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input placeholder="Question" value={form.question} onChange={(e) => set("question", e.target.value)} data-testid="admin-faq-question" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <textarea placeholder="Answer" value={form.answer} onChange={(e) => set("answer", e.target.value)} data-testid="admin-faq-answer" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} />
        <button onClick={add} data-testid="admin-faq-add" className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add FAQ</button>
      </div>
      <div className="space-y-2">
        {data.map((f) => (
          <div key={f.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div><p className="text-sm font-bold text-slate-800">{f.question}</p><p className="mt-1 text-sm text-slate-500">{f.answer}</p></div>
              <div className="flex shrink-0 items-center gap-2"><Switch checked={f.is_active} onCheckedChange={() => toggle(f)} /><button onClick={() => del(f.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSupport() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-support"], queryFn: () => api.get("/admin/support").then((r) => r.data) });
  const set = async (id, status) => { await api.patch(`/admin/support/${id}`, { status }); qc.invalidateQueries({ queryKey: ["a-support"] }); toast.success("Updated"); };
  if (!data) return <Spinner />;
  if (!data.length) return <EmptyState icon={LifeBuoy} title="No support tickets" />;
  return (
    <div className="space-y-3">
      {data.map((t) => (
        <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-slate-800">{t.subject} <span className="ml-1 text-xs text-slate-400">· {t.customer_name}</span></p>
            <select value={t.status} onChange={(e) => set(t.id, e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs">
              {["open", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <p className="mt-1 text-sm text-slate-600">{t.messages?.[0]?.text}</p>
        </div>
      ))}
    </div>
  );
}

function AdminAgents() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-agents"], queryFn: () => api.get("/admin/agents").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Fill all fields");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post("/admin/agents", form); qc.invalidateQueries({ queryKey: ["a-agents"] }); toast.success("Support agent created"); setForm({ name: "", email: "", password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (a) => { await api.patch(`/admin/agents/${a.id}`, { is_active: !a.is_active }); qc.invalidateQueries({ queryKey: ["a-agents"] }); toast.success(a.is_active ? "Access revoked" : "Access granted"); };
  const reset = async (a) => { const p = window.prompt(`New password for ${a.name} (min 6 chars)`); if (!p) return; if (p.length < 6) return toast.error("Min 6 characters"); await api.patch(`/admin/agents/${a.id}`, { password: p }); toast.success("Password reset"); };
  const del = async (a) => { if (!window.confirm(`Remove agent ${a.name}? They will lose access immediately.`)) return; await api.delete(`/admin/agents/${a.id}`); qc.invalidateQueries({ queryKey: ["a-agents"] }); toast.success("Agent removed"); };
  if (!data) return <Spinner />;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input placeholder="Agent name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name-input" className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Email (login id)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email-input" className="w-52 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agent-password-input" className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={create} data-testid="create-agent-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#0EA5E9] px-4 py-2 text-sm font-bold text-white"><UserPlus className="h-4 w-4" /> Create agent</button>
      </div>
      <p className="mb-3 flex items-center gap-2 text-sm text-slate-500"><Headset className="h-4 w-4 text-[#0EA5E9]" /> Support agents have <b className="mx-1">limited access</b> — they can only view and reply to customer support chats, nothing else.</p>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Agent</Th><Th>Login Email</Th><Th>Access</Th><Th>Open Chats</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {data.map((a) => (
            <tr key={a.id} className="border-b border-slate-50" data-testid="agent-row">
              <Td className="font-semibold text-slate-800">{a.name}</Td>
              <Td className="text-slate-500">{a.email}</Td>
              <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${a.is_active ? "bg-brand-soft text-brand" : "bg-red-50 text-red-500"}`}>{a.is_active ? "Active" : "Revoked"}</span></Td>
              <Td>{a.open_chats}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Switch checked={a.is_active} onCheckedChange={() => toggle(a)} data-testid="agent-toggle" />
                  <button onClick={() => reset(a)} title="Reset password" className="text-slate-400 hover:text-slate-700"><KeyRound className="h-4 w-4" /></button>
                  <button onClick={() => del(a)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </Td>
            </tr>
          ))}
          {!data.length && <tr><Td className="text-slate-400" >No agents yet. Create one above.</Td></tr>}
        </tbody>
      </Table>
    </div>
  );
}

function AdminApp() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-apk"], queryFn: () => api.get("/admin/apk").then((r) => r.data) });
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  const fmtSize = (b) => (!b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

  const upload = async () => {
    if (!file) return toast.error("Choose an .apk file");
    if (!file.name.toLowerCase().endsWith(".apk")) return toast.error("Only .apk files are allowed");
    if (!version.trim()) return toast.error("Enter a version");
    const fd = new FormData();
    fd.append("file", file); fd.append("version", version); fd.append("notes", notes);
    setUploading(true);
    try {
      await api.post("/admin/apk", fd);
      qc.invalidateQueries({ queryKey: ["a-apk"] });
      toast.success("APK uploaded & enabled");
      setFile(null); setVersion(""); setNotes("");
    } catch (e) { toast.error(apiError(e)); }
    setUploading(false);
  };
  const toggle = async (enabled) => { await api.patch("/admin/apk-config", { apk_enabled: enabled }); qc.invalidateQueries({ queryKey: ["a-apk"] }); toast.success(enabled ? "APK download enabled" : "APK download disabled"); };
  const del = async () => { await api.delete("/admin/apk"); qc.invalidateQueries({ queryKey: ["a-apk"] }); toast.success("APK removed"); };

  if (!data) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* PWA info */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-2 flex items-center gap-2 font-head font-bold text-slate-900"><Smartphone className="h-5 w-5 text-brand" /> PWA Installation</h3>
        <p className="text-sm text-slate-500">Grofkart is a fully installable Progressive Web App. Customers see an <b>Install App</b> button in the header and on their Account page (native prompt on Android, Add-to-Home-Screen guide on iPhone). No configuration needed here.</p>
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
          <li className="flex items-center gap-2"><PackageCheck className="h-4 w-4 text-brand" /> Manifest, service worker & icons active</li>
          <li className="flex items-center gap-2"><PackageCheck className="h-4 w-4 text-brand" /> Standalone full-screen mode</li>
          <li className="flex items-center gap-2"><PackageCheck className="h-4 w-4 text-brand" /> Offline fallback caching</li>
        </ul>
      </div>

      {/* APK management */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 font-head font-bold text-slate-900"><Upload className="h-5 w-5 text-brand" /> Android APK</h3>
        <p className="mb-3 text-sm text-slate-500">Upload a genuine signed APK. The customer "Download APK" button only appears while an APK is uploaded and enabled.</p>

        {data.apk_available ? (
          <div className="mb-3 rounded-xl border border-brand/20 bg-brand-soft/50 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-800">{data.filename}</p>
                <p className="text-xs text-slate-500">v{data.version} · {fmtSize(data.size)}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">{data.apk_enabled ? "Live" : "Hidden"}</span>
                <Switch data-testid="admin-apk-toggle" checked={data.apk_enabled} onCheckedChange={toggle} />
                <button onClick={del} data-testid="admin-apk-delete" className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {data.notes && <p className="mt-2 text-xs text-slate-500">{data.notes}</p>}
          </div>
        ) : (
          <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-500">No APK uploaded. Customers currently see only the PWA install option.</div>
        )}

        <div className="space-y-2">
          <input type="file" accept=".apk,application/vnd.android.package-archive" data-testid="admin-apk-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand file:px-3 file:py-1 file:text-white" />
          <div className="flex gap-2">
            <input placeholder="Version (e.g. 1.0.0)" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="admin-apk-version" className="w-36 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Release notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-apk-notes" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={upload} disabled={uploading} data-testid="admin-apk-upload"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">
            {uploading ? <Spinner className="h-4 w-4 border-white" /> : <Upload className="h-4 w-4" />} {data.apk_available ? "Replace APK" : "Upload APK"}
          </button>
          <p className="text-[11px] text-slate-400">Only valid .apk (ZIP-signed) files are accepted. Do not upload fake files.</p>
        </div>
      </div>
    </div>
  );
}

function AdminAudit() {
  const { data } = useQuery({ queryKey: ["a-audit"], queryFn: () => api.get("/admin/audit-logs").then((r) => r.data) });
  if (!data) return <Spinner />;
  if (!data.length) return <EmptyState icon={ScrollText} title="No audit logs yet" subtitle="Admin actions will be logged here." />;
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Admin</Th><Th>Action</Th><Th>Changes</Th><Th>Time</Th></tr></thead>
      <tbody>
        {data.map((l, i) => (
          <tr key={i} className="border-b border-slate-50">
            <Td className="text-slate-600">{l.admin}</Td><Td className="font-semibold text-slate-800">{l.action}</Td>
            <Td className="font-mono text-xs text-slate-500">{JSON.stringify(l.changes)}</Td>
            <Td className="text-xs text-slate-400">{new Date(l.at).toLocaleString("en-IN")}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AdminVerification() {
  const { data } = useQuery({ queryKey: ["a-verification"], queryFn: () => api.get("/admin/verification-payments").then((r) => r.data) });
  if (!data) return <Spinner />;
  const total = data.reduce((s, p) => s + (p.amount || 0), 0);
  if (!data.length) return <EmptyState icon={BadgeCheck} title="No verification payments yet" subtitle="Retailer ₹11 verification and ₹2 mandate authentication payments appear here." />;
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Total Collected" value={rupee(total)} icon={IndianRupee} color="#059669" />
        <StatCard label="Verification Fees" value={data.filter((p) => p.type === "verification").length} icon={BadgeCheck} color="#8B5CF6" />
        <StatCard label="Mandate Auth (₹2)" value={data.filter((p) => p.type === "mandate_auth").length} icon={BadgeCheck} color="#3B82F6" />
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Store</Th><Th>Type</Th><Th>Amount</Th><Th>Txn ID</Th><Th>Status</Th><Th>Date</Th></tr></thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.id} className="border-b border-slate-50" data-testid="admin-verification-row">
              <Td className="font-semibold text-slate-800">{p.store_name}</Td>
              <Td className="capitalize text-slate-500">{(p.type || "verification").replace(/_/g, " ")}</Td>
              <Td className="font-mono">{rupee(p.amount)}</Td>
              <Td className="font-mono text-xs text-slate-500">{p.txn_id}</Td>
              <Td><span className="rounded-md bg-brand-soft px-2 py-0.5 text-[11px] font-bold text-brand">{p.status}</span></Td>
              <Td className="text-xs text-slate-400">{new Date(p.created_at).toLocaleString("en-IN")}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const FEE_FIELDS = [
  { k: "retailer_verification_fee", label: "Retailer Verification Fee (₹)" },
  { k: "mandate_auth_amount", label: "Mandate Auth Amount (₹)" },
  { k: "trial_days", label: "Free Trial (days)" },
  { k: "retailer_delivery_fee", label: "Retailer Delivery Fee — settlement (₹)" },
  { k: "platform_fee", label: "Customer Platform Fee (₹)" },
  { k: "delivery_base_fee", label: "Customer Delivery Fee (₹)" },
  { k: "free_delivery_threshold", label: "Free Delivery Threshold (₹)" },
  { k: "tax_percent", label: "Tax (%)" },
  { k: "sub_base", label: "Subscription Base (₹)" },
  { k: "sub_gst_percent", label: "Subscription GST (%)" },
];

function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [form, setForm] = useState(null);
  React.useEffect(() => { if (data && !form) setForm(data); }, [data, form]);
  if (!data || !form) return <Spinner />;
  const save = async () => {
    const payload = {};
    FEE_FIELDS.forEach((f) => { payload[f.k] = Number(form[f.k]); });
    try { await api.patch("/admin/settings", payload); qc.invalidateQueries({ queryKey: ["a-settings"] }); toast.success("Settings updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-2xl">
      <div className="mb-4 flex items-center gap-2"><Settings className="h-5 w-5 text-brand" /><h3 className="font-head font-bold text-slate-900">Fees, Delivery Deduction & Trial</h3></div>
      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-2">
        {FEE_FIELDS.map((f) => (
          <label key={f.k} className="text-sm">
            <span className="mb-1 block font-semibold text-slate-600">{f.label}</span>
            <input type="number" value={form[f.k] ?? ""} data-testid={`settings-${f.k}`}
              onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-brand" />
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="settings-save-btn" className="mt-4 rounded-xl bg-brand px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-deep">Save settings</button>
    </div>
  );
}
